/**
 * The little color math the appearance needs: sRGB hex in and out, mixing in Oklab (so steps
 * between two colors look even) and the WCAG contrast ratio.
 */
export type Rgba = Readonly<{ r: number; g: number; b: number; a: number }>;

const HEX = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/** `#rgb`, `#rgba`, `#rrggbb` or `#rrggbbaa`; anything else is `null`. */
export function parseHex(value: string | null | undefined): Rgba | null {
  const match = HEX.exec(value?.trim() ?? '');
  if (!match?.[1]) return null;
  let digits = match[1];
  if (digits.length <= 4) digits = [...digits].map((digit) => digit + digit).join('');
  const channel = (index: number) => Number.parseInt(digits.slice(index, index + 2), 16) / 255;
  return { r: channel(0), g: channel(2), b: channel(4), a: digits.length === 8 ? channel(6) : 1 };
}

/** Lowercase `#rrggbb`, or `#rrggbbaa` when not opaque. */
export function toHex(color: Rgba): string {
  const byte = (value: number) =>
    Math.round(Math.min(1, Math.max(0, value)) * 255)
      .toString(16)
      .padStart(2, '0');
  const rgb = `#${byte(color.r)}${byte(color.g)}${byte(color.b)}`;
  return color.a >= 1 ? rgb : `${rgb}${byte(color.a)}`;
}

/** A 6-digit hex color, normalized to lowercase; `null` for anything else. */
export function normalizeOpaqueHex(value: string | null | undefined): string | null {
  const color = parseHex(value);
  return color && color.a >= 1 ? toHex(color) : null;
}

const toLinear = (value: number) =>
  value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
const fromLinear = (value: number) =>
  value <= 0.0031308 ? value * 12.92 : 1.055 * value ** (1 / 2.4) - 0.055;

type Oklab = { l: number; a: number; b: number };

function toOklab(color: Rgba): Oklab {
  const r = toLinear(color.r);
  const g = toLinear(color.g);
  const b = toLinear(color.b);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return {
    l: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  };
}

function fromOklab({ l, a, b }: Oklab, alpha: number): Rgba {
  const l3 = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m3 = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s3 = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return {
    r: fromLinear(4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3),
    g: fromLinear(-1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3),
    b: fromLinear(-0.0041960863 * l3 - 0.7034186147 * m3 + 1.707614701 * s3),
    a: alpha,
  };
}

/** `amount` of `toward` mixed into `from`, in Oklab (0 keeps `from`, 1 gives `toward`). */
export function mix(from: string, toward: string, amount: number): string {
  const a = parseHex(from);
  const b = parseHex(toward);
  if (!a || !b) return from;
  const t = Math.min(1, Math.max(0, amount));
  const x = toOklab(a);
  const y = toOklab(b);
  return toHex(
    fromOklab(
      { l: x.l + (y.l - x.l) * t, a: x.a + (y.a - x.a) * t, b: x.b + (y.b - x.b) * t },
      a.a + (b.a - a.a) * t,
    ),
  );
}

/** The color at the given opacity. */
export function withAlpha(color: string, alpha: number): string {
  const parsed = parseHex(color);
  return parsed ? toHex({ ...parsed, a: Math.min(1, Math.max(0, alpha)) }) : color;
}

/** Oklab lightness, 0 (black) to 1 (white). */
export function lightness(color: string): number {
  const parsed = parseHex(color);
  return parsed ? toOklab(parsed).l : 0;
}

/** Oklab chroma: about 0 for grays, up to ~0.37 for the most vivid sRGB colors. */
export function chroma(color: string): number {
  const parsed = parseHex(color);
  if (!parsed) return 0;
  const { a, b } = toOklab(parsed);
  return Math.hypot(a, b);
}

/** Oklch hue in degrees, or `null` for a gray. */
export function hue(color: string): number | null {
  const parsed = parseHex(color);
  if (!parsed) return null;
  const { a, b } = toOklab(parsed);
  if (Math.hypot(a, b) < 0.02) return null;
  return ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360;
}

function luminance(color: Rgba): number {
  return 0.2126 * toLinear(color.r) + 0.7152 * toLinear(color.g) + 0.0722 * toLinear(color.b);
}

/** WCAG contrast ratio between two opaque colors, 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const x = parseHex(a);
  const y = parseHex(b);
  if (!x || !y) return 1;
  const [light, dark] = [luminance(x), luminance(y)].sort((p, q) => q - p) as [number, number];
  return (light + 0.05) / (dark + 0.05);
}

/** `color` moved toward `toward` until it reaches `ratio` against `background` (or `toward`). */
export function ensureContrast(
  color: string,
  background: string,
  toward: string,
  ratio: number,
): string {
  for (let step = 0; step <= 10; step += 1) {
    const candidate = mix(color, toward, step / 10);
    if (contrastRatio(candidate, background) >= ratio) return candidate;
  }
  return toward;
}
